import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { visitorNavigateArgs } from "../../lib/visitor-nav";
import type { VisitorTab } from "../../lib/visitor-search";

interface CategoryNavigationOptions {
  orgSlug: string;
  mapSlug: string;
  categoryParam: string | undefined;
  viewParam: "list" | undefined;
  tabParam: VisitorTab;
  embedParam: "true" | undefined;
  categories: Array<{ slug: string; isDefault?: boolean }> | undefined;
  hasMapData: boolean;
  /** Preserve list param during initial category redirect */
  listParam?: string;
}

export function useCategoryNavigation({
  orgSlug,
  mapSlug,
  categoryParam,
  viewParam,
  tabParam,
  embedParam,
  categories,
  hasMapData,
  listParam,
}: CategoryNavigationOptions) {
  const navigate = useNavigate();
  const [pendingCategory, setPendingCategory] = useState<string | null>(null);

  // Compute default and valid categories
  const defaultCategory = useMemo(
    () => categories?.find((c) => c.isDefault)?.slug ?? "all",
    [categories]
  );

  const validCategorySlugs = useMemo(
    () => new Set(categories?.map((c) => c.slug) ?? []),
    [categories]
  );

  // Validate current category param
  const selectedCategory = useMemo(() => {
    if (categoryParam === undefined) return undefined;
    if (categoryParam === "all") return "all";
    if (categoryParam === "saved") return "saved";
    if (categoryParam === "happening-now") return "happening-now";
    if (validCategorySlugs.has(categoryParam)) return categoryParam;
    return null; // invalid
  }, [categoryParam, validCategorySlugs]);

  // Redirect on invalid/missing category
  useEffect(() => {
    if (!hasMapData) return;
    if (categoryParam === undefined) {
      navigate({
        ...visitorNavigateArgs(orgSlug, mapSlug),
        search: {
          tab: tabParam,
          category: defaultCategory,
          poi: undefined,
          view: viewParam,
          ref: undefined,
          list: listParam,
          embed: embedParam,
          poiFilter: undefined,
        },
        replace: true,
      });
    } else if (selectedCategory === null) {
      navigate({
        ...visitorNavigateArgs(orgSlug, mapSlug),
        search: {
          tab: tabParam,
          category: "all",
          poi: undefined,
          view: viewParam,
          ref: undefined,
          list: listParam,
          embed: embedParam,
          poiFilter: undefined,
        },
        replace: true,
      });
    }
  }, [
    categoryParam,
    defaultCategory,
    navigate,
    orgSlug,
    mapSlug,
    selectedCategory,
    hasMapData,
    viewParam,
    tabParam,
    embedParam,
    listParam,
  ]);

  // Clear pending when URL catches up
  useEffect(() => {
    if (pendingCategory && categoryParam === pendingCategory) {
      setPendingCategory(null);
    }
  }, [categoryParam, pendingCategory]);

  // Effective category: pending (sync) takes precedence over URL (async)
  const effectiveCategory = pendingCategory ?? selectedCategory;

  // Navigate to category
  const navigateToCategory = (
    categorySlug: string,
    options?: { forceListView?: boolean }
  ) => {
    if (options?.forceListView) {
      setPendingCategory(categorySlug);
    }

    navigate({
      ...visitorNavigateArgs(orgSlug, mapSlug),
      search: {
        tab: tabParam,
        category: categorySlug,
        poi: undefined,
        view: options?.forceListView ? "list" : viewParam,
        ref: undefined,
        list: undefined,
        embed: embedParam,
        poiFilter: undefined,
      },
    });
  };

  return {
    selectedCategory,
    effectiveCategory,
    defaultCategory,
    validCategorySlugs,
    navigateToCategory,
  };
}
